import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Autocomplete, createTheme, TextField, Tooltip} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {makeStyles} from '@mui/styles';
import {bgColorRequester, bgColorSpender, useDebounce} from '../../_helpers';
import {TooltipWrapperIssue} from '../../TooltipWrapperIssue.jsx';
import {NO_OPTIONS_LABEL, NO_OPTIONS_ARRAY, LAMBDA_NAMES} from '../../../utils/constants';
import {getUserEmailsGraphAPILambda, getUsersGraphAPILambda, toErrorFlow} from '../../../utils/functionSignatures';
import {BrickContext} from 'olympe';

/**
 * @param {UserField.propTypes} props
 * @return {JSX.Element}
 */
export function UserField(props) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [textInput, setTextInput] = useState(props.initialValue);
    const debouncedTextInput = useDebounce(textInput, 800);
    const [queriedText, setQueriedText] = useState('');
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const [showErrorTooltip, setShowErrorTooltip] = useState(false);
    const [error, setError] = useState({code: 200, body: ''});

    const getUsersGraphAPI = props.getUsersAPI ? props.getUsersAPI : props.context.get(LAMBDA_NAMES.GET_USERS_GRAPH_API);
    const getUsersByEmailGraphAPI = props.context.get(LAMBDA_NAMES.GET_USERS_BY_EMAIL_GRAPH_API);

    // This useEffect handles the queries to perform to get the options for the autocomplete
    // it will initiate queries when textInput changes (with debouncing)
    useEffect(() => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }
        if (!debouncedTextInput) {
            return;
        }

        const searchInput = debouncedTextInput.split('-')[0].trim();
        // avoid performing a query of with the same input as the last query
        if (queriedText === searchInput && options.length > 0) {
            return;
        }
        // avoid performing a query when the autocomplete is closed
        if (open === false) {
            return;
        }
        // avoid performing a query when the input text is < 3 characters
        if (searchInput.length < 3) {
            setOptions([{label: 'Min. 3 characters', value: ''}]);
            return;
        }
        // start a query
        setIsPerformingQuery(true);
        setShowErrorTooltip(false);

        (async () => {
            const emptyResult = ['{"value":[]}'];
            const runIdOrEmailSearch = !!getUsersByEmailGraphAPI && !searchInput.includes(' ');
            let nameSearchError = null;
            Promise.all([
                getUsersGraphAPILambda(props.context, getUsersGraphAPI, searchInput)
                    .catch((reason) => {
                        if (!runIdOrEmailSearch) {
                            throw reason;
                        }
                        console.error(reason);
                        nameSearchError = reason;
                        return emptyResult;
                    }),
                runIdOrEmailSearch ?
                    getUserEmailsGraphAPILambda(props.context, getUsersByEmailGraphAPI, searchInput.replace(/'/g, '\'\''))
                        .catch((reason) => {
                            console.error(reason);
                            return emptyResult;
                        }) :
                    emptyResult,
            ])
                .then(([byName, byIdOrEmail]) => {
                    const result = [...JSON.parse(byName[0]).value, ...JSON.parse(byIdOrEmail[0]).value]
                        .filter((userInfo) => !!userInfo['onPremisesSamAccountName'])
                        .filter((userInfo, index, self) => index === self.findIndex((u) =>
                            u['onPremisesSamAccountName'].toUpperCase() === userInfo['onPremisesSamAccountName'].toUpperCase()))
                        .map((userInfo) => {
                            return {
                                label: `${userInfo['givenName']} ${userInfo['surname']}`,
                                value: userInfo['onPremisesSamAccountName'].toUpperCase(),
                                email: userInfo['mail'],
                            };
                        });
                    // Remove the duplicates
                    const resultWithoutDuplicates = result.map((value, index, self) => {
                        if (self.filter((t) => t.label === value.label).length > 1) {
                            return {
                                ...value,
                                label: `${value.label} - ${value.value}`,
                            };
                        } else {
                            return value;
                        }
                    });

                    // Sort alphabetically
                    const sortedResults = resultWithoutDuplicates.sort((a, b) => a.label.localeCompare(b.label));
                    if (sortedResults.length === 0 && nameSearchError) {
                        throw nameSearchError;
                    }

                    setOptions(sortedResults.length > 0 ? sortedResults : NO_OPTIONS_ARRAY);
                    setQueriedText(searchInput);
                    setIsPerformingQuery(false);
                })
                .catch((reason) => {
                    setIsPerformingQuery(false);
                    const errorFlow = toErrorFlow(reason, 1005);
                    setError({code: errorFlow.getCode(), body: errorFlow.getMessage()});
                    setShowErrorTooltip(true);
                    console.error(reason);
                });
        })();
    }, [debouncedTextInput, open, queriedText]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        setTextInput(inputValue);
    };

    const handleChange = (event, newValue) => {
        if (!newValue) {
            setTextInput('');
        }
        if (!newValue) {
            props.onChange({label: '', value: '', email: ''});
        } else if (typeof newValue === 'string') {
            props.onChange({label: newValue, value: newValue, email: ''});
        } else {
            props.onChange(newValue);
        }
    };

    const _theme = createTheme();
    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                },
            };
        },
        {_theme},
    );
    const classes = useStyles();

    return (
        <div className={classes.root}>
            <TooltipWrapperIssue
                error={error}
                open={showErrorTooltip}
                onClose={() => setShowErrorTooltip(false)}
            >
                <Autocomplete
                    data-cy={'userField_' + props.userRole}
                    data-user-id={props.wholeValue?.value || ''}
                    forcePopupIcon
                    fullWidth
                    freeSolo
                    sx={{
                        ...props.sx,
                        backgroundColor: props.mandatoryForSpender && props.highlightMandatoryFieldForSpender ?
                            bgColorSpender :
                            props.mandatoryForRequester && props.highlightMandatoryFieldForRequester ?
                                bgColorRequester :
                                null,
                    }}
                    size={'small'}
                    open={open}

                    options={options}
                    getOptionDisabled={(option) => option.label === NO_OPTIONS_LABEL || option.label === 'Min. 3 characters'}
                    value={props.initialValue}
                    onChange={handleChange}
                    filterOptions={(options) => options}
                    disabled={props?.disabled}

                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}

                    loading={isPerformingQuery}
                    renderInput={(params) => (
                        <Tooltip title={props.wholeValue?.value ? <div> {props.wholeValue.value} <br/>{props.wholeValue.email}</div> : '' } placement={'top'} arrow>
                            <TextField
                                {...params}
                                data-cy={'userField_' + props.userRole + '_input'}
                                data-user-id={props.wholeValue?.value || ''}
                                variant={'outlined'}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <React.Fragment>
                                            {isPerformingQuery || textInput !== debouncedTextInput || props.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.startAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                                onChange={handleInputChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                        </Tooltip>

                    )}
                    renderOption={(propsOption, option) => (
                        <li {...propsOption} data-cy={`userField_${props.userRole}_option`}>
                            {option.label}
                        </li>
                    )}
                />
            </TooltipWrapperIssue>
        </div>
    );
}

UserField.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    initialValue: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    getUsersAPI: PropTypes.object,
    sx: PropTypes.object,
    mandatoryForRequester: PropTypes.bool,
    mandatoryForSpender: PropTypes.bool,
    highlightMandatoryFieldForRequester: PropTypes.bool,
    highlightMandatoryFieldForSpender: PropTypes.bool,
    userRole: PropTypes.string,
    userIdToCallSAPAPIs: PropTypes.string,
    wholeValue: PropTypes.object,
};
